import "leaflet/dist/leaflet.css"
import "./index.css"

import { useCallback, useEffect, useState } from "react"
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet"

import L from "leaflet"
import { css } from "@emotion/css"
import icon from "assets/Pin_default.svg"
import iconShadow from "leaflet/dist/images/marker-shadow.png"
import { MarkersRenderer } from "./Markers"
import { LookupDataType, RegionType } from "../../types"
import { useRevenueParams } from "../../hooks/useRevenueParams"

const styles = {
  map: css`
    padding: 0px;
  `,
  leafletContainer: css`
    height: 100%;
    margin: 0px auto;
  `,
}

type SetViewPropTypes = {
  animate: boolean
  zoom: number
  lat: number
  lng: number
}

type MapState = {
  lat: number
  lng: number
  zoom: number
}

type RevenueState = {
  map: MapState
}

const initialState: RevenueState = {
  map: {
    lat: 31,
    lng: -14,
    zoom: 3,
  },
}

export default function WorldMap({
  lookups,
  updatedAt,
  showDetails,
}: {
  lookups?: LookupDataType
  updatedAt: number
  showDetails: boolean
}) {
  const [state, setState] = useState(initialState)
  const { regionParam, regionEnabled, marketEnabled } = useRevenueParams()

  const SetView = (props: SetViewPropTypes) => {
    const { lat, lng, zoom, animate } = props

    const map = useMap()
    map.setView({ lat: lat, lng: lng }, zoom, {
      animate: animate || false,
    })
    showDetails ? map.dragging.disable() : map.dragging.enable()

    return null
  }

  let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
  })
  L.Marker.prototype.options.icon = DefaultIcon

  const markers = MarkersRenderer()

  const getRegion = useCallback(() => {
    const region: RegionType | undefined = lookups?.regions?.find(
      (r) => r.name.toLowerCase() === regionParam?.toLowerCase(),
    )

    return region
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt, regionParam])

  useEffect(() => {
    const four = regionEnabled || marketEnabled
    const zoom = four ? 4 : 3
    const initial = { lat: initialState.map.lat, lng: initialState.map.lng }
    const region = getRegion()

    const coords = {
      lat: !!region?.lat ? parseFloat(region.lat) : initial.lat,
      lng: !!region?.lng ? parseFloat(region.lng) : initial.lng,
    }
    setState((s) => {
      return { ...s, map: { ...s.map, ...coords, zoom } }
    })
  }, [getRegion, marketEnabled, regionEnabled])

  return (
    <MapContainer
      className={styles.leafletContainer}
      center={{ lat: state.map.lat, lng: state.map.lng }}
      zoom={state.map.zoom}
      zoomControl={false}
      doubleClickZoom={false}
      scrollWheelZoom={false}
      maxBounds={[
        [-80, -260],
        [76, 280],
      ]}
      maxBoundsViscosity={1}
    >
      <TileLayer minZoom={2} maxZoom={4} url="/tiles/{z}/{x}/{y}.png" />

      <SetView
        lat={state.map.lat}
        lng={state.map.lng}
        zoom={state.map.zoom}
        animate={true}
      />

      {markers.length > 0 &&
        markers.map((marker, idx: number) => (
          <Marker
            key={`marker-${idx}`}
            position={{ lat: Number(marker.lat!), lng: Number(marker.lng!) }}
          >
            {!showDetails && <Popup>{marker.body}</Popup>}
          </Marker>
        ))}
    </MapContainer>
  )
}
